import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import FlagIcon from '@mui/icons-material/Flag';
import CampaignIcon from '@mui/icons-material/Campaign';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import MessagingService from '../services/MessagingService';

const formatMessageTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const MessagingPage = () => {
  const [reports, setReports] = useState([]);
  const [threads, setThreads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [audience, setAudience] = useState('all_creators');
  const [campaignBody, setCampaignBody] = useState('');
  const [targetId, setTargetId] = useState('');
  const [sending, setSending] = useState(false);

  const loadInbox = async () => {
    try {
      const data = await MessagingService.listConversations();
      setThreads(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Impossible de charger le support');
      setThreads([]);
    }
  };

  const loadReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await MessagingService.listReports();
      setReports(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Impossible de charger les signalements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInbox();
    loadReports();
  }, []);

  const openThread = async (id) => {
    setSelectedId(id);
    try {
      const data = await MessagingService.getConversation(id);
      setThread(data);
      loadInbox();
    } catch (err) {
      toast.error('Conversation introuvable');
      setThread(null);
    }
  };

  const handleReply = async (event) => {
    event.preventDefault();
    if (!selectedId || !reply.trim()) {
      return;
    }
    try {
      await MessagingService.sendReply(selectedId, reply.trim());
      setReply('');
      toast.success('Réponse envoyée');
      await openThread(selectedId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Envoi impossible');
    }
  };

  const handleHide = async (report) => {
    if (!report.messageId) {
      toast.error('Ce signalement n’est pas lié à un message');
      return;
    }
    try {
      await MessagingService.hideMessage(report.messageId, report.id);
      toast.success('Message masqué');
      loadReports();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Masquage impossible');
    }
  };

  const handleCampaign = async (event) => {
    event.preventDefault();
    setSending(true);
    try {
      const userAccountId = audience === 'user' ? Number(targetId) || null : null;
      const shopId = audience === 'shop' ? Number(targetId) || null : null;
      const result = await MessagingService.sendCampaign(
        audience,
        campaignBody,
        userAccountId,
        shopId
      );
      toast.success(`Campagne envoyée (${result?.recipientCount ?? 0} destinataire(s))`);
      setCampaignBody('');
      setTargetId('');
      loadInbox();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Envoi de campagne impossible');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="animate-in space-y-10">
      <section>
        <h1 className="text-3xl font-bold text-white mb-2">Messagerie</h1>
        <p className="text-surface-400">
          Support créateurs (mise en relation, boutique, fiche), file des signalements (LCEN) et envois Ritaaa en fils 1:1.
          Pas de suivi de commande : Ritaaa n’en a pas encore. Les messages sont masqués, pas réécrits. Conservation 24 mois.
        </p>
      </section>

      <section className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-surface-800 flex items-center gap-2">
          <SupportAgentIcon className="w-5 h-5 text-surface-400" />
          <h2 className="text-lg font-semibold text-white">Support créateurs</h2>
        </div>
        <div className="grid gap-6 p-6 lg:grid-cols-[minmax(220px,280px)_1fr]">
          <ul className="border border-surface-800 rounded-xl overflow-hidden">
            {threads.length === 0 && (
              <li className="px-4 py-3 text-sm text-surface-500">Aucun message support.</li>
            )}
            {threads.map((item) => (
              <li key={item.id} className="border-b border-surface-800 last:border-b-0">
                <button
                  type="button"
                  className={`w-full text-left px-4 py-3 ${item.id === selectedId ? 'bg-surface-800' : 'hover:bg-surface-900'}`}
                  onClick={() => openThread(item.id)}
                >
                  <strong className="block text-white">{item.label}</strong>
                  <time className="block text-xs text-surface-400" dateTime={item.lastMessageAt}>
                    {formatMessageTime(item.lastMessageAt)}
                  </time>
                </button>
              </li>
            ))}
          </ul>
          <div>
            {!thread && <p className="text-surface-500">Sélectionnez une conversation.</p>}
            {thread && (
              <>
                <h3 className="text-white font-semibold mb-4">{thread.label}</h3>
                {(thread.messages || []).map((message) => (
                  <article
                    key={message.id}
                    className={`rounded-xl border p-4 mb-3 ${
                      message.authorType === 'platform'
                        ? 'border-emerald-700 bg-emerald-950/40'
                        : 'border-surface-800 bg-surface-900'
                    }`}
                  >
                    <header className="flex flex-wrap items-baseline gap-2 text-xs text-surface-400 mb-2">
                      <span className="font-semibold text-white">
                        {message.authorLabel || (message.authorType === 'platform' ? 'Ritaaa' : 'Créateur')}
                      </span>
                      {message.authorType === 'platform' ? (
                        <span className="uppercase tracking-wide text-[10px] font-bold bg-emerald-700 text-white rounded-full px-1.5 py-0.5 inline-flex items-center gap-1" title="Compte officiel Ritaaa">
                          <span aria-hidden="true">🛡️</span>
                          Officiel
                        </span>
                      ) : null}
                      <time dateTime={message.createdAt}>{formatMessageTime(message.createdAt)}</time>
                    </header>
                    <p className="whitespace-pre-wrap text-surface-100">{message.body}</p>
                  </article>
                ))}
                <form onSubmit={handleReply} className="space-y-3 mt-4">
                  <textarea
                    required
                    maxLength={4000}
                    rows={4}
                    className="w-full bg-surface-900 border border-surface-700 rounded-lg px-3 py-2 text-white"
                    value={reply}
                    onChange={(event) => setReply(event.target.value)}
                    placeholder="Répondre au nom de Ritaaa"
                  />
                  <button type="submit" className="btn-primary">
                    Répondre
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-surface-800 flex items-center gap-2">
          <FlagIcon className="w-5 h-5 text-surface-400" />
          <h2 className="text-lg font-semibold text-white">Signalements ouverts</h2>
        </div>
        {loading && <p className="p-6 text-surface-400">Chargement…</p>}
        {error && <p className="p-6 text-red-400">{error}</p>}
        {!loading && !error && reports.length === 0 && (
          <p className="p-6 text-surface-500">Aucun signalement ouvert.</p>
        )}
        {!loading && !error && reports.length > 0 && (
          <ul className="divide-y divide-surface-800">
            {reports.map((report) => (
              <li key={report.id} className="px-6 py-4 flex items-start gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-white">Fil #{report.conversationId}</p>
                  <p className="text-sm text-surface-400 whitespace-pre-wrap">{report.reason}</p>
                  <p className="text-xs text-surface-500 mt-1">
                    {report.createdAt ? new Date(report.createdAt).toLocaleString('fr-FR') : ''}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  onClick={() => handleHide(report)}
                >
                  Masquer le message
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card p-6">
        <div className="flex items-center gap-2 mb-4">
          <CampaignIcon className="w-5 h-5 text-surface-400" />
          <h2 className="text-lg font-semibold text-white">Campagne Ritaaa</h2>
        </div>
        <p className="text-sm text-surface-400 mb-4">
          Chaque destinataire reçoit un fil privé avec Ritaaa. Il répond à Ritaaa, pas au groupe.
        </p>
        <form onSubmit={handleCampaign} className="space-y-4">
          <label className="block text-sm text-surface-300">
            Audience
            <select
              className="mt-1 w-full bg-surface-900 border border-surface-700 rounded-lg px-3 py-2 text-white"
              value={audience}
              onChange={(event) => setAudience(event.target.value)}
            >
              <option value="all_creators">Tous les créateurs</option>
              <option value="all_customers">Tous les acheteurs</option>
              <option value="user">Un compte (id)</option>
              <option value="shop">Une boutique (id)</option>
            </select>
          </label>
          {(audience === 'user' || audience === 'shop') && (
            <label className="block text-sm text-surface-300">
              Identifiant
              <input
                className="mt-1 w-full bg-surface-900 border border-surface-700 rounded-lg px-3 py-2 text-white"
                value={targetId}
                onChange={(event) => setTargetId(event.target.value)}
                required
              />
            </label>
          )}
          <label className="block text-sm text-surface-300">
            Message
            <textarea
              required
              maxLength={4000}
              rows={5}
              className="mt-1 w-full bg-surface-900 border border-surface-700 rounded-lg px-3 py-2 text-white"
              value={campaignBody}
              onChange={(event) => setCampaignBody(event.target.value)}
            />
          </label>
          <button type="submit" className="btn-primary" disabled={sending}>
            {sending ? 'Envoi…' : 'Envoyer'}
          </button>
        </form>
      </section>
    </div>
  );
};

export default MessagingPage;
